<template>
  <div class="weapon_wrap">
    <div class="weapon-info container">
      <div class="item-image">
        <Thumb
            :alt="weaponTitle"
            :fontSize="25"
            :height="25"
            :image-height="250"
            :image-width="350"
            :item="weapon as Item"
            :size="25"
            :src="getImageFromWaxpeer(weapon.full_name || '', '500:500')"
            image-sizes="500:500"
            loading="eager"
        />
      </div>
      <div class="item-info">
        <div class="item-info__header">
          <h1 class="item-name">
            <span>{{ weaponTitle }}</span>
          </h1>
          <!--          <client-only>-->
          <TeamIcons
              v-if="data?.characteristics.side"
              :team="data.characteristics.side"
          />
          <SpecTeams
              v-if="filters.teams?.length > 1"
              :filters="filters"
          />
          <!--          </client-only>-->
        </div>
        <span class="item-description">
          {{ t(`weapon_page_title`, {name: t('filters.weapon.' + weapon?.weapon)}) }}
        </span>

        <div v-if="data" class="item-tags">
          <div
              v-if="data?.characteristics.reload_time"
              class="card card-light"
          >
            <p class="tag-title">{{ t('reload_time') }}</p>
            <div class="f fs gap8">
              <svg class="tag-svg" height="16" width="16">
                <use xlink:href="/icons/sprite.svg#clock"></use>
              </svg>
              <span class="tag-desc">
                {{ t('reload', {time: data?.characteristics.reload_time}) }}
              </span>
            </div>
          </div>
          <div
              v-if="data?.characteristics.magazine"
              class="card card-light"
          >
            <p class="tag-title">{{ t('magazine_capacity') }}</p>
            <div class="f fs gap8">
              <svg class="tag-svg" height="20" width="20">
                <use xlink:href="/icons/sprite.svg#capacity"></use>
              </svg>
              <span class="tag-desc">{{ data?.characteristics.magazine }}</span>
            </div>
          </div>
          <div v-if="data?.characteristics.accuracy_range" class="card card-light">
            <p class="tag-title">{{ t('accuracy_range') }}</p>
            <div class="f fs gap8">
              <svg class="tag-svg" height="20" width="20">
                <use xlink:href="/icons/sprite.svg#ruler"></use>
              </svg>
              <span class="tag-desc">
                {{ t('range', {n: data?.characteristics.accuracy_range}) }}
              </span>
            </div>
          </div>
          <div v-if="data?.characteristics.rate_of_fire" class="card card-light">
            <p class="tag-title">{{ t('rate_of_fire') }}</p>
            <div class="f fs gap8">
              <svg class="tag-svg" height="16" width="16">
                <use xlink:href="/icons/sprite.svg#flame"></use>
              </svg>
              <span class="tag-desc">
                {{ t('fire_rate', {n: data?.characteristics.rate_of_fire}) }}
              </span>
            </div>
          </div>
        </div>
        <!--        client-only-->
        <template v-else>
          <div class="specifications hm">
            <!--            <div v-if="filters.teams?.length > 1"-->
            <!--                 class="card card-dark p4">-->
            <!--              <SpecTeams-->
            <!--                  :filters="filters"-->
            <!--              />-->
            <!--            </div>-->
            <div v-if="showCategories"
                 class="card card-dark p4">
              <SpecCategories
                  :filters="filters"
              />
            </div>

            <div v-if="filters.rarities_count?.length > 1"
                 class="card card-dark p4">
              <SpecRarities
                  :filters="filters"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <div v-if="pending" class="load_period loading-container mt-10">
      <div class="f fc">
        <i class="i-loading ml10"></i>
      </div>
    </div>
    <div
        v-else
        :class="{container: !data && items.length <= 7, p0: currW < 1000 }"
        class="pb60 f fs ft gap32 wrapper">
      <!--      <client-only>-->
      <PagesWeaponComponentsSpecifications
          v-if="data || Object.keys(filters)?.length"
          :data="data as WeapoData"
          :filters="filters"
          :name="dataName"
      />
      <!--      </client-only>-->

      <div v-if="items.length" class="fg weapon-items">
        <UiItemsGrid>
          <LazyPagesWeaponComponentsSkinCard
              v-for="item in items"
              :key="item?.market_hash_name"
              :item="item"
              :link="getItemLinkWithoutExt({
                name: getName(item.market_hash_name),
                slug: item.link
              })"
          ></LazyPagesWeaponComponentsSkinCard>
        </UiItemsGrid>

        <PaginateV2
            key="paginate"
            v-model="pagination.page"
            :pages-length="pagination.pagesLength"
            @on-page-change="emit('on-page-change', $event)"
        />
      </div>
      <LazyUiNoResult v-if="!pending && !items?.length" class="mxa" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import Thumb from '~/components/Pages/Item/Components/Children/Thumb.vue'
import {useI18n} from 'vue-i18n'
import type {
  Item,
  ItemsByField,
  ItemWeaponPageFiltersResult
} from "~/graphql/generated/schema-types";
import {getImageFromWaxpeer} from "~/utils/get-image-url";
import TeamIcons from "~/components/Pages/Weapon/Components/TeamIcons.vue";
import weaponsData from '~/data/weapons-data.json'
import type {WeapoData} from "~/types/WeapoData";
import {useResize} from "~/composables/resize";
import SpecRarities from "~/components/Pages/Weapon/Components/SpecRarities.vue";
import SpecCategories from "~/components/Pages/Weapon/Components/SpecCategories.vue";
import SpecTeams from "~/components/Pages/Weapon/Components/SpecTeams.vue";
import {getItemLinkWithoutExt} from "~/utils/name-utils";

const emit = defineEmits(['on-page-change'])
const props = defineProps<{
  weapon: Item & { category: string },
  pending: boolean,
  items: ItemsByField[],
  filters: ItemWeaponPageFiltersResult,
  pagination: {
    page: number
    pagesLength: number
  }
}>()

const currW = useResize()
const {t} = useI18n()
const data = ref<WeapoData | null>(null)
const dataName = ref<string>(getModifiedString(props.weapon.weapon).replace('-', '_'))

const showCategories = computed(() => {
  const filters = props.filters;
  return [filters.normal_count, filters.souvenir_count, filters.stattrak_count].filter(count => !!count)?.length > 1
})

const getName = (itemName: string) => {
  if (!itemName.includes('Sticker')) {
    return itemName?.replace(/(?:^\s*|\s*\([^)]*\)\s*|\s*$)/g, '')
  }
  return itemName
}

data.value = weaponsData[dataName.value as keyof typeof weaponsData] || null

const weaponTitle = computed(() => {
  if (props.weapon.category?.toLowerCase() === 'other') {
    return t('filters.weapon.' + props.weapon?.weapon)
  }

  if (props.weapon?.full_name.toLowerCase().includes('collect')) {
    return `${t('inspect_all_skins', {name: t('filters.weapon.' + props.weapon?.weapon)})}`
  }

  return `${t('all_skins', {name: t('filters.weapon.' + props.weapon?.weapon)})}`
})
</script>

<style lang="scss" scoped>


.weapon {
  &_wrap {
    margin-top: 3rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 7rem;

    @media screen and (max-width: 1250px) {
      margin-top: 0;
    }
  }

  .specifications {
    &:deep(.weapon-specifications__item) {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .item {
        &-title {
          font-weight: 800;
          font-size: 18px;
          text-align: center;
        }

        &-labels {
          display: flex;
          //flex-direction: column;
          gap: 8px;
          flex-wrap: wrap;

          &-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border: 1px solid var(--surfaces-high-container);
            padding: 0.1rem 1rem;
            min-height: 3.8rem;

            &.active {
              border-color: var(--accent-secondary);
            }
          }

          &-left {
            &.circle {
              display: flex;
              align-items: center;
              gap: 8px;

              &:before {
                content: '';
                display: inline-block;
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background-color: var(--destructive);
              }

              &.circle-covert:before,
              &.circle-remarkable:before,
              &.circle-distinguished:before {
                background-color: var(--destructive);
              }

              &.circle-classified:before,
              &.circle-exotic:before,
              &.circle-exceptional:before {
                background-color: #D63EEA;
              }

              &.circle-restricted:before,
              &.circle-high-grade:before,
              &.circle-master:before {
                background-color: #9861FF;
              }

              &.circle-mil-spec-grade:before,
              &.circle-mil-spec:before,
              &.circle-superior:before {
                background-color: #617BFF;
              }

              &.circle-industrial:before,
              &.circle-industrial-grade:before,
              &.circle-extraordinary:before {
                background-color: #5E98D9;
              }

              &.circle-consumer:before,
              &.circle-consumer-grade:before,
              &.circle-default:before {
                background-color: #B0C3D9;
              }
            }
          }
        }
      }
    }
  }

  &-info {
    width: 100%;
    display: flex;
    gap: 4.8rem;
    color: var(--surfaces-on-surface);

    .item-image {
      width: 38.4rem;
      height: 38.4rem;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      @media screen and (max-width: 525px) {
        width: 100%;
        min-width: 100%;
      }
    }

    .item-info {
      display: flex;
      flex-direction: column;
      gap: 20px;

      &__header {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .item-name {
        font-weight: 600;
        font-size: 2.8rem;
        line-height: 3.8rem;
      }

      .item-description {
        font-size: 1.6rem;
        opacity: 0.8;
        max-width: 68rem;
      }

      .item-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 1.6rem;

        .card {
          padding: 12px;
          min-width: 160px;
          border: 1px solid var(--surfaces-highest-container);
        }

        .tag-title {
          font-size: 1.4rem;
          line-height: 2.1rem;
          font-weight: 500;
          color: var(--surfaces-on-surface-variable);
          margin-bottom: 8px;
        }

        .tag-svg {
          color: var(--accent-primary);
        }

        .tag-desc {
          font-weight: 600;
          font-size: 14px;
        }
      }
    }
  }
}

@media screen and (max-width: 1075px) {
  .weapon {
    .weapon_wrap {
      gap: 4rem;
    }

    &-info {
      .item-name {
        font-size: 2.4rem;
        line-height: 3.4rem;
      }

      .item-description {
        font-size: 1.4rem;
        line-height: 3rem;
      }
    }
  }
}

@media screen and (max-width: 979px) {
  .weapon-items {
    padding-right: 16px;
    padding-left: 16px;
  }
}

@media screen and (max-width: 850px) {
  .weapon-info {
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .item-info {
      align-items: center;
      width: 100%;

      .item-tags {
        flex-wrap: nowrap;
        justify-content: center;
        overflow-x: auto;
        width: 100%;
        padding-bottom: 10px;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .weapon {
    .weapon_wrap {
      gap: 4rem;
    }

    .fg {
      width: 100%;
    }

    .wrapper {
      flex-direction: column;
    }

    &-info {
      gap: 0;

      .item-info {
        margin-left: 0;
        margin-top: 1.6rem;

        .item-tags {
          justify-content: flex-start;
          margin: 0 16px;
          width: unset;
          max-width: calc(100% + 30px);
          padding-right: 16px;
          padding-left: 16px;
        }

        .item-name {
          font-size: 2.2rem;
          line-height: 3.2rem;
        }

        .item-description {
          margin-top: 1.2rem;
          line-height: 2.8rem;
        }
      }
    }
  }
}

@media screen and (max-width: 525px) {
  .weapon {
    .weapon_wrap {
      gap: 1.6rem;
    }

    &-info {
      .item-image {
        margin-left: 1.5rem;
        margin-right: 1.5rem;
        background: rgba(33, 40, 53, 0.2);
        border-radius: 1.2rem;
      }

      .item-info {
        .item-name {
          font-size: 2rem;
          line-height: 2.7rem;
        }

        .item-description {
          margin-top: 0.8rem;
          line-height: 2.6rem;
        }
      }
    }
  }
}
</style>
